import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Audit } from '@vsolv/core/audit/domain';
import { User } from '@vsolv/core/users/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { PaginationConfig } from '@vsolv/vectors/table';
import { Warranty } from '@wsphere/warranties/domain';
import { BehaviorSubject, combineLatest, from, map, Observable, of, shareReplay, switchMap, tap } from 'rxjs';
import { WarrantyWebService } from '../../../warranty/services';

type AuditWithUser = Audit.AuditLogDto & { _user?: User.Model | null };

type AuditResponseWithUser = Omit<Audit.Model, 'items'> & {
  items: AuditWithUser[];
};

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-warranty-audit-page',
  templateUrl: './warranty-audit.page.html',
  standalone: false,
})
export class WarrantyAuditPage implements OnInit {
  @Input() warranty$!: Observable<Warranty.Model | null>;

  constructor(
    private warrantySvc: WarrantyWebService,
    private toastSvc: ToastService
  ) {}

  protected loaded$ = new BehaviorSubject<boolean>(false);

  protected readonly paginationQueryRequest$ = new BehaviorSubject<{ page: number; limit: number }>({
    page: 1,
    limit: 10,
  });

  protected audits$!: Observable<AuditResponseWithUser | null>;
  protected paginationConfig$!: Observable<PaginationConfig | null>;

  ngOnInit() {
    this.audits$ = combineLatest([this.warranty$, this.paginationQueryRequest$]).pipe(
      tap(() => this.loaded$.next(false)),
      switchMap(([warranty, pagination]) => {
        if (!warranty) {
          this.loaded$.next(true);
          return of<AuditResponseWithUser | null>(null);
        }

        return from(this.warrantySvc.listAudit(warranty.id, pagination) as Promise<AuditResponseWithUser>).pipe(
          switchMap(res => {
            if (!res?.items?.length) {
              return of(res);
            }

            const userMap: Record<string, User.Model | null> = {};

            const userIds = Array.from(
              new Set(res.items.map(a => a.changedBy).filter((id): id is string => typeof id === 'string' && !!id))
            );

            return from(
              Promise.all(
                userIds.map(async id => {
                  try {
                    const user = await this.warrantySvc.getAuditUser(id);
                    return [id, user as User.Model | null] as const;
                  } catch (e) {
                    console.error('Failed to load audit user', id, e);
                    return [id, null] as const;
                  }
                })
              )
            ).pipe(
              map(entries => {
                for (const [id, user] of entries) {
                  userMap[id] = user;
                }

                res.items.forEach(audit => {
                  const id = audit.changedBy;
                  audit._user = id && typeof id === 'string' ? (userMap[id] ?? null) : null;
                });

                return res;
              })
            );
          }),
          tap({
            next: () => this.loaded$.next(true),
            error: () => this.loaded$.next(true),
          })
        );
      }),
      shareReplay(1)
    );

    this.paginationConfig$ = this.audits$.pipe(
      map((audits): PaginationConfig | null => {
        if (!audits?.meta) return null;
        return {
          totalItems: audits.meta.totalItems,
          itemsPerPage: audits.meta.itemsPerPage,
          currentPage: audits.meta.currentPage,
          pageSizes: [10, 20, 50],
        };
      })
    );
  }

  protected refresh() {
    // Trigger a refresh by re-emitting the same pagination request
    const current = this.paginationQueryRequest$.value;
    this.paginationQueryRequest$.next({ ...current });
  }

  protected onPageChange(event: { currentPage: number; itemsPerPage: number }) {
    this.paginationQueryRequest$.next({
      page: event.currentPage,
      limit: event.itemsPerPage,
    });
  }

  protected getInitials(user: User.Model | null): string {
    if (!user?.displayName) return 'S';
    const trimmed = user.displayName.trim();
    if (!trimmed) return 'S';
    return trimmed.charAt(0).toUpperCase();
  }
}
